import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, MapPin, ExternalLink } from 'lucide-react';

const Experience = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const experiences = [
    {
      id: 1,
      title: 'Python Developer',
      company: 'Gokul Infocare Pvt Ltd (Australia)',
      location: 'Remote',
      period: 'March - June 2024',
      description: 'Developed and maintained scalable web applications using Python, and AWS. Created interactive frontends and backends using Tkinter, Streamlit and Figma designs for company\'s operations.',
      achievements: [
        'Built an Image-to-MCQ Pipeline that automates document digitization and assessment creation',
        'Designed and implemented a Hospital Appointment System for efficient scheduling and management',
        'Developed optimized web scraping scripts to collect data from multiple external websites for internal analysis',
        'Created interactive frontends and backends using Tkinter, Streamlit and Figma designs for company operations',
        'Increased application performance by 40% through optimization'
      ],
      technologies: ['Python', 'Tkinter', 'Streamlit', 'AWS', 'Web Scraping', 'Figma', 'PostgreSQL'],
    },
    {
      id: 2,
      title: 'Research Assistant - Computational NeuroScience',
      company: 'Exploratory Project under Prof. Dr. JAC Fredo AR',
      location: 'IIT BHU Varanasi',
      period: 'October 2023 - April 2024',
      description: 'Conducted advanced research in Computational NeuroScience focusing on Autism Spectrum Disorder (ASD) classification using functional MRI data analysis and machine learning techniques.',
      achievements: [
        'Published research paper "Diagnostic Classification of ASD Improves with Dynamic Functional Connectivity (FC) of fMRI Compared to Static FC" in ISBS-RMBS 2024',
        'Conducted fMRI analysis on 112 subjects, distinguishing subjects with ASD from TD using sFC and dFC techniques',
        'Applied advanced machine learning methods including MLP, XGBoost and Logistic Regression',
        'Achieved 88.76% accuracy on static FC and remarkable 96.65% accuracy on dynamic FC',
        'Demonstrated significant improvement in ASD diagnostic classification through dynamic connectivity analysis'
      ],
      technologies: ['Python', 'fMRI Analysis', 'Machine Learning', 'MLP', 'XGBoost', 'Logistic Regression', 'Computational NeuroScience', 'Data Analysis'],
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
    },
  };

  return (
    <section id="experience" className="py-20 bg-white dark:bg-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold mb-6 gradient-text"
          >
            Work Experience
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
          >
            My professional journey and the experiences that shaped my career
          </motion.p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-primary-500 to-primary-300"></div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-12"
          >
            {experiences.map((experience, index) => (
              <motion.div
                key={experience.id}
                variants={itemVariants}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } flex-col md:flex-row`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-dark-900 shadow-lg z-10"></div>

                {/* Content Card */}
                <motion.div
                  whileHover={{ scale: 1.02, y: -5 }}
                  className={`w-full md:w-5/12 ${
                    index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                  } ml-12 md:ml-0`}
                >
                  <div className="bg-gray-50 dark:bg-dark-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                          {experience.title}
                        </h3>
                        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold mb-2">
                          <ExternalLink size={16} />
                          {experience.company}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        {experience.period}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin size={16} />
                        {experience.location}
                      </div>
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                      {experience.description}
                    </p>

                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Key Achievements:
                      </h4>
                      <ul className="space-y-1">
                        {experience.achievements.map((achievement, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2"
                          >
                            <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mt-16"
        >
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-white rounded-full font-semibold text-lg shadow-lg hover:bg-primary-600 transition-colors duration-200"
          >
            Let's Work Together
            <ExternalLink size={20} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;